import React, { useState, useEffect, useCallback } from 'react';
import { X, Share2, Users, Link as LinkIcon, Copy, Check, Mail, Calendar, Eye, Download, Edit, Trash2, User as UserIcon, Lock, Clock, BarChart, MessageSquare } from 'lucide-react';
import { Button } from './ui/Button';
import { permissionApi, authApi, publicLinkApi, slackApi, teamApi, getAccessToken } from '../lib/api';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: 'file' | 'folder';
  resourceId: string;
  resourceIds?: string[];
  resourceName: string;
}

interface Permission {
  id: string;
  resource_type: 'file' | 'folder';
  resource_id: string;
  grantee_type: 'user' | 'team';
  grantee_id: string;
  grantee_name: string;
  permission_type: 'view' | 'download' | 'edit' | 'delete';
  granted_at: string;
  expires_at?: string | null;
  is_public_link?: boolean;
}

interface User {
  id: string;
  name: string;
  email: string;
}

interface Team {
  id: string;
  name: string;
  description?: string;
}

export function ShareDialog({
  isOpen,
  onClose,
  resourceType,
  resourceId,
  resourceIds,
  resourceName
}: ShareDialogProps) {
  const [activeTab, setActiveTab] = useState<'people' | 'link' | 'slack'>('people');
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Share with people form state
  const [shareType, setShareType] = useState<'user' | 'team'>('user');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<'view' | 'download' | 'edit' | 'delete'>>(new Set(['view' as const]));
  const [expiresAt, setExpiresAt] = useState('');
  const [notifyViaSlack, setNotifyViaSlack] = useState(false);
  const [slackConnected, setSlackConnected] = useState(false);
  const [slackConnectedUsers, setSlackConnectedUsers] = useState<any[]>([]);
  const [selectedSlackUsers, setSelectedSlackUsers] = useState<Set<string>>(new Set());
  const [showSlackSelector, setShowSlackSelector] = useState(false);
  const [sendingSlackNotifications, setSendingSlackNotifications] = useState(false);

  // Link sharing state
  const [publicLink, setPublicLink] = useState<any>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkExpires, setLinkExpires] = useState('');
  const [disableDownload, setDisableDownload] = useState(false);
  const [maxViews, setMaxViews] = useState('');
  const [linkStats, setLinkStats] = useState<any>(null);
  const [loadingLink, setLoadingLink] = useState(false);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await permissionApi.getResourcePermissions(resourceType, resourceId);
      const perms = response.data.data || [];
      setPermissions(perms);

      // Find public link if exists
      const publicLinkPerm = perms.find((p: any) => p.is_public_link);
      if (publicLinkPerm) {
        setPublicLink(publicLinkPerm);
        fetchLinkStats(publicLinkPerm.id);
      }
    } catch (err: any) {
      console.error('Failed to fetch permissions:', err);
      setError(err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [resourceType, resourceId]);

  const fetchLinkStats = async (linkId: string) => {
    try {
      const response = await publicLinkApi.getStats(linkId);
      setLinkStats(response.data.data);
    } catch (err) {
      console.error('Failed to fetch link stats:', err);
    }
  };

  const fetchUsersAndTeams = useCallback(async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        authApi.getUsers(),
        teamApi.getUserTeams()
      ]);
      setAllUsers((usersRes.data.data || []).map((u: any) => ({
        id: u.id,
        name: u.name,
        email: u.email
      })));
      setAllTeams((teamsRes.data.data || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        description: t.description
      })));
    } catch (err) {
      console.error('Failed to fetch users and teams:', err);
    }
  }, []);

  const checkSlackStatus = useCallback(async () => {
    try {
      console.log('🔍 ShareDialog: Checking Slack connection status...');
      const response = await slackApi.getStatus();
      console.log('🔍 ShareDialog: Slack status response:', response.data);
      const isConnected = response.data.connected || false;
      console.log('🔍 ShareDialog: Setting slackConnected to:', isConnected);
      setSlackConnected(isConnected);
    } catch (err) {
      console.error('❌ ShareDialog: Failed to check Slack status:', err);
      setSlackConnected(false);
    }
  }, []);

  const fetchSlackConnectedUsers = useCallback(async () => {
    try {
      console.log('📋 ShareDialog: Fetching Slack-connected users...');
      const response = await slackApi.getConnectedUsers();
      console.log('📋 ShareDialog: Slack-connected users:', response.data.data);
      setSlackConnectedUsers(response.data.data || []);
    } catch (err) {
      console.error('❌ ShareDialog: Failed to fetch Slack users:', err);
      setSlackConnectedUsers([]);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchPermissions();
      fetchUsersAndTeams();
      checkSlackStatus();
      fetchSlackConnectedUsers();
    }
  }, [isOpen, fetchPermissions, fetchUsersAndTeams, checkSlackStatus, fetchSlackConnectedUsers]);

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedIds.size === 0) {
      setError('Please select at least one user');
      return;
    }

    if (selectedPermissions.size === 0) {
      setError('Please select at least one permission');
      return;
    }

    setSharing(true);
    setError('');
    setSuccess('');

    try {
      // Share with all selected users across all resource IDs
      const allResourceIds = resourceIds && resourceIds.length > 0 ? resourceIds : [resourceId];
      const grantPromises: Promise<any>[] = [];
      for (const resId of allResourceIds) {
        for (const userId of Array.from(selectedIds)) {
          for (const permType of Array.from(selectedPermissions)) {
            grantPromises.push(
              permissionApi.grant({
                resource_type: resourceType,
                resource_id: resId,
                grantee_type: shareType,
                grantee_id: userId,
                permission_type: permType,
                expires_at: expiresAt || undefined
              })
            );
          }
        }
      }
      await Promise.all(grantPromises);

      // Send Slack notification if requested
      if (notifyViaSlack) {
        try {
          const fileUrl = resourceType === 'file'
            ? `${window.location.origin}/media/${resourceId}`
            : `${window.location.origin}/media?folderId=${resourceId}`;

          await slackApi.sendManualNotification({
            userIds: Array.from(selectedIds),
            fileName: resourceName,
            fileUrl
          });
        } catch (slackErr) {
          console.error('Failed to send Slack notification:', slackErr);
        }
      }

      const userCount = selectedIds.size;
      const fileCount = allResourceIds.length;
      setSuccess(`Shared ${fileCount} file${fileCount > 1 ? 's' : ''} with ${userCount} user${userCount > 1 ? 's' : ''}!${notifyViaSlack ? ' Slack notification sent.' : ''}`);
      setSelectedIds(new Set());
      setSelectedPermissions(new Set(['view' as const]));
      setExpiresAt('');
      setNotifyViaSlack(false);
      await fetchPermissions();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Failed to share:', err);
      setError(err.response?.data?.error || 'Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const handleRevoke = async (permissionId: string, granteeName: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove access for ${granteeName}?`)) return;

    try {
      setError('');
      await permissionApi.revoke(permissionId);
      setSuccess('Access removed successfully!');
      await fetchPermissions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Failed to revoke permission:', err);
      setError(err.response?.data?.error || 'Failed to remove access');
    }
  };

  const handleSendSlackNotifications = async () => {
    if (selectedSlackUsers.size === 0) {
      setError('Please select at least one person to notify');
      return;
    }

    try {
      setSendingSlackNotifications(true);
      setError('');

      const fileUrl = resourceType === 'file'
        ? `${window.location.origin}/media/${resourceId}`
        : `${window.location.origin}/media?folderId=${resourceId}`;

      console.log('📬 ShareDialog: Sending Slack notifications...', {
        userIds: Array.from(selectedSlackUsers),
        fileName: resourceName,
        fileUrl
      });

      const response = await slackApi.sendManualNotification({
        userIds: Array.from(selectedSlackUsers),
        fileName: resourceName,
        fileUrl
      });

      console.log('📬 ShareDialog: Slack notification response:', response.data);

      setSuccess(response.data.message || 'Slack notifications sent!');
      setSelectedSlackUsers(new Set());
      setShowSlackSelector(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('❌ ShareDialog: Failed to send Slack notifications:', err);
      setError(err.response?.data?.error || 'Failed to send Slack notifications');
    } finally {
      setSendingSlackNotifications(false);
    }
  };

  const toggleSlackUser = (userId: string) => {
    setSelectedSlackUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleGenerateLink = async () => {
    try {
      setLoadingLink(true);
      setError('');

      // First create a permission if one doesn't exist
      let permId = permissions.find(p => p.resource_type === resourceType && p.resource_id === resourceId)?.id;

      if (!permId) {
        // Create a permission for the current user to generate the public link
        const meResponse = await fetch('/api/auth/me', { headers: { 'Authorization': `Bearer ${getAccessToken()}` } });
        const meData = await meResponse.json();
        const currentUserId = meData?.data?.user?.id || meData?.data?.id || meData?.user?.id;
        if (!currentUserId) throw new Error('Could not determine current user');
        const permResponse = await permissionApi.grant({
          resource_type: resourceType,
          resource_id: resourceId,
          grantee_type: 'user',
          grantee_id: currentUserId,
          permission_type: 'view'
        });
        permId = permResponse.data.data.id;
      }

      // Ensure permId is defined
      if (!permId) {
        throw new Error('Failed to get or create permission');
      }

      // Create public link
      const response = await publicLinkApi.create(permId, {
        password: linkPassword || undefined,
        expires_at: linkExpires || undefined,
        disable_download: disableDownload,
        max_views: maxViews ? parseInt(maxViews) : undefined
      });

      setPublicLink(response.data.data);
      setSuccess('Public link created successfully!');
      await fetchPermissions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Failed to generate link:', err);
      setError(err.response?.data?.error || 'Failed to generate link');
    } finally {
      setLoadingLink(false);
    }
  };

  const handleUpdateLink = async () => {
    if (!publicLink) return;

    try {
      setLoadingLink(true);
      setError('');

      await publicLinkApi.update(publicLink.id, {
        password: linkPassword || undefined,
        expires_at: linkExpires || undefined,
        disable_download: disableDownload,
        max_views: maxViews ? parseInt(maxViews) : undefined
      });

      setSuccess('Link settings updated!');
      await fetchPermissions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Failed to update link:', err);
      setError(err.response?.data?.error || 'Failed to update link');
    } finally {
      setLoadingLink(false);
    }
  };

  const handleRevokeLink = async () => {
    if (!publicLink) return;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to revoke this public link? It will no longer be accessible.')) return;

    try {
      setLoadingLink(true);
      setError('');

      await publicLinkApi.revoke(publicLink.id);
      setPublicLink(null);
      setLinkStats(null);
      setLinkPassword('');
      setLinkExpires('');
      setDisableDownload(false);
      setMaxViews('');
      setSuccess('Public link revoked successfully!');
      await fetchPermissions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Failed to revoke link:', err);
      setError(err.response?.data?.error || 'Failed to revoke link');
    } finally {
      setLoadingLink(false);
    }
  };

  const handleCopyLink = async () => {
    if (!publicLink) return;

    try {
      const link = `${window.location.origin}/s/${publicLink.id}`;
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const getPermissionIcon = (permType: string) => {
    switch (permType) {
      case 'view':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'download':
        return <Download className="w-4 h-4 text-green-500" />;
      case 'edit':
        return <Edit className="w-4 h-4 text-orange-500" />;
      case 'delete':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      default:
        return <UserIcon className="w-4 h-4 text-gray-400" />;
    }
  };

  const getPermissionLabel = (permType: string) => {
    switch (permType) {
      case 'view':
        return 'Can view';
      case 'download':
        return 'Can download';
      case 'edit':
        return 'Can edit';
      case 'delete':
        return 'Can delete';
      default:
        return permType;
    }
  };

  // Show ALL users and teams in dropdown, not just those without permissions
  // Users can share with the same person multiple times with different permission types
  const availableUsers = allUsers;
  const availableTeams = allTeams;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Share {resourceType}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">{resourceName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('people')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'people'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <Users className="w-4 h-4 inline-block mr-2" />
            Share with people
          </button>
          <button
            onClick={() => setActiveTab('link')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'link'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <LinkIcon className="w-4 h-4 inline-block mr-2" />
            Get link
          </button>
          {slackConnectedUsers.length > 0 && (
            <button
              onClick={() => setActiveTab('slack')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'slack'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <MessageSquare className="w-4 h-4 inline-block mr-2" />
              Notify via Slack
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Success/Error Messages */}
          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3">
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* People Tab */}
          {activeTab === 'people' && (
            <>
              {/* Share Form */}
              <form onSubmit={handleShare} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    Add people or teams
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {/* Share type toggle: Users or Teams */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setShareType('user'); setSelectedIds(new Set()); }}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border ${shareType === 'user' ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      <UserIcon className="w-3.5 h-3.5" />
                      Users
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShareType('team'); setSelectedIds(new Set()); }}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border ${shareType === 'team' ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                      <Users className="w-3.5 h-3.5" />
                      Teams
                    </button>
                  </div>

                  {/* Multi-select User Selection with Search */}
                  {shareType === 'user' && (
                  <div>
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      disabled={sharing}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 mb-1"
                    />
                    {selectedIds.size > 0 && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">{selectedIds.size} user(s) selected</p>
                    )}
                    <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                      {availableUsers.filter(u => {
                        if (!userSearch.trim()) return true;
                        const s = userSearch.toLowerCase();
                        return u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
                      }).map(user => (
                        <label
                          key={user.id}
                          className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 ${selectedIds.has(user.id) ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(user.id)}
                            onChange={() => {
                              const next = new Set(selectedIds);
                              if (next.has(user.id)) next.delete(user.id);
                              else next.add(user.id);
                              setSelectedIds(next);
                            }}
                            disabled={sharing}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-900 dark:text-white">{user.name}</span>
                          <span className="text-gray-500 text-xs">({user.email})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  )}

                  {/* Multi-select Team Selection */}
                  {shareType === 'team' && (
                  <div>
                    {selectedIds.size > 0 && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">{selectedIds.size} team(s) selected</p>
                    )}
                    <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                      {availableTeams.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No teams available. Create a team first.</p>
                      ) : (
                        availableTeams.map(team => (
                          <label
                            key={team.id}
                            className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 ${selectedIds.has(team.id) ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(team.id)}
                              onChange={() => {
                                const next = new Set(selectedIds);
                                if (next.has(team.id)) next.delete(team.id);
                                else next.add(team.id);
                                setSelectedIds(next);
                              }}
                              disabled={sharing}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-900 dark:text-white">{team.name}</span>
                            {team.description && (
                              <span className="text-gray-500 text-xs truncate">({team.description})</span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                  )}

                  {/* Permission Selection */}
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Select permissions
                    </label>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer p-2 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <input
                          type="checkbox"
                          checked={selectedPermissions.has('view')}
                          onChange={(e) => {
                            const newPerms = new Set(selectedPermissions);
                            if (e.target.checked) {
                              newPerms.add('view');
                            } else {
                              newPerms.delete('view');
                            }
                            setSelectedPermissions(newPerms);
                          }}
                          disabled={sharing}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Eye className="w-4 h-4 text-blue-500" />
                        Can view
                      </label>

                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer p-2 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <input
                          type="checkbox"
                          checked={selectedPermissions.has('download')}
                          onChange={(e) => {
                            const newPerms = new Set(selectedPermissions);
                            if (e.target.checked) {
                              newPerms.add('download');
                            } else {
                              newPerms.delete('download');
                            }
                            setSelectedPermissions(newPerms);
                          }}
                          disabled={sharing}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Download className="w-4 h-4 text-green-500" />
                        Can download
                      </label>

                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer p-2 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <input
                          type="checkbox"
                          checked={selectedPermissions.has('edit')}
                          onChange={(e) => {
                            const newPerms = new Set(selectedPermissions);
                            if (e.target.checked) {
                              newPerms.add('edit');
                            } else {
                              newPerms.delete('edit');
                            }
                            setSelectedPermissions(newPerms);
                          }}
                          disabled={sharing}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Edit className="w-4 h-4 text-orange-500" />
                        Can edit
                      </label>

                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer p-2 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <input
                          type="checkbox"
                          checked={selectedPermissions.has('delete')}
                          onChange={(e) => {
                            const newPerms = new Set(selectedPermissions);
                            if (e.target.checked) {
                              newPerms.add('delete');
                            } else {
                              newPerms.delete('delete');
                            }
                            setSelectedPermissions(newPerms);
                          }}
                          disabled={sharing}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Trash2 className="w-4 h-4 text-red-500" />
                        Can delete
                      </label>
                    </div>

                    <Button
                      type="submit"
                      size="sm"
                      className="w-full"
                      disabled={sharing || selectedIds.size === 0 || selectedPermissions.size === 0}
                    >
                      {sharing ? 'Sharing...' : 'Share'}
                    </Button>
                  </div>

                  {/* Optional: Expiration Date */}
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      <Calendar className="w-3 h-3 inline-block mr-1" />
                      Expires at (optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      disabled={sharing}
                      className="w-full h-9 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Slack Notification Option */}
                  {(() => {
                    const shouldShow = slackConnected && shareType === 'user';
                    console.log('🔍 ShareDialog: Slack checkbox visibility check:', {
                      slackConnected,
                      shareType,
                      shouldShow
                    });
                    return shouldShow && (
                      <div className="flex items-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md">
                        <input
                          type="checkbox"
                          id="notifySlack"
                          checked={notifyViaSlack}
                          onChange={(e) => setNotifyViaSlack(e.target.checked)}
                          disabled={sharing}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <label htmlFor="notifySlack" className="flex items-center gap-2 text-sm text-purple-900 dark:text-purple-100 cursor-pointer">
                          <MessageSquare className="w-4 h-4" />
                          Send Slack notification
                        </label>
                      </div>
                    );
                  })()}
                </div>
              </form>

              {/* Shared With List */}
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
              ) : permissions.length === 0 ? (
                <div className="text-center py-8">
                  <Share2 className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 dark:text-gray-400">Not shared with anyone yet</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                    Share this {resourceType} to collaborate
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {permissions.length} {permissions.length === 1 ? 'Person' : 'People'} with access
                  </h3>

                  {permissions.map((perm) => (
                    <div
                      key={perm.id}
                      className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {/* Icon */}
                        <div className="flex-shrink-0">
                          {perm.grantee_type === 'team' ? (
                            <Users className="w-4 h-4 text-purple-500" />
                          ) : (
                            <UserIcon className="w-4 h-4 text-blue-500" />
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {perm.grantee_name}
                            </p>
                            <span className="flex-shrink-0 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-1">
                              {getPermissionIcon(perm.permission_type)}
                              {getPermissionLabel(perm.permission_type)}
                            </span>
                          </div>
                          {perm.expires_at && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Expires: {new Date(perm.expires_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRevoke(perm.id, perm.grantee_name)}
                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Remove access"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Link Tab */}
          {activeTab === 'link' && (
            <div className="space-y-4">
              {!publicLink ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <LinkIcon className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Create public link
                    </h3>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Anyone with the link can access this {resourceType}
                  </p>

                  {/* Link Settings */}
                  <div className="space-y-3 mb-4">
                    {/* Password Protection */}
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                        <Lock className="w-4 h-4" />
                        Password protection (optional)
                      </label>
                      <input
                        type="password"
                        value={linkPassword}
                        onChange={(e) => setLinkPassword(e.target.value)}
                        placeholder="Enter password"
                        className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Expiration */}
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                        <Clock className="w-4 h-4" />
                        Expiration date (optional)
                      </label>
                      <input
                        type="datetime-local"
                        value={linkExpires}
                        onChange={(e) => setLinkExpires(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Disable Download */}
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={disableDownload}
                        onChange={(e) => setDisableDownload(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Download className="w-4 h-4" />
                      Disable downloads
                    </label>

                    {/* Max Views */}
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                        <Eye className="w-4 h-4" />
                        Maximum views (optional)
                      </label>
                      <input
                        type="number"
                        value={maxViews}
                        onChange={(e) => setMaxViews(e.target.value)}
                        placeholder="Unlimited"
                        min="1"
                        className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleGenerateLink}
                    className="w-full"
                    disabled={loadingLink}
                  >
                    {loadingLink ? 'Generating...' : (
                      <>
                        <LinkIcon className="w-4 h-4 mr-2" />
                        Generate public link
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Link Display */}
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Check className="w-4 h-4 text-green-600" />
                      <h3 className="text-sm font-semibold text-green-900 dark:text-green-100">
                        Public link active
                      </h3>
                    </div>

                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={`${window.location.origin}/s/${publicLink.id}`}
                        readOnly
                        className="flex-1 h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                      />
                      <Button
                        onClick={handleCopyLink}
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0"
                      >
                        {linkCopied ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Link Stats */}
                    {linkStats && (
                      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-green-200 dark:border-green-800">
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                            <Eye className="w-3 h-3" />
                            Views
                          </div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                            {linkStats.view_count || 0}
                            {linkStats.max_views && ` / ${linkStats.max_views}`}
                          </p>
                        </div>
                        {linkStats.last_viewed_at && (
                          <div>
                            <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                              <Clock className="w-3 h-3" />
                              Last viewed
                            </div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                              {new Date(linkStats.last_viewed_at).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Link Settings */}
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                      Link settings
                    </h4>

                    <div className="space-y-3">
                      {/* Password Protection */}
                      <div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                          <Lock className="w-4 h-4" />
                          Password protection
                        </label>
                        <input
                          type="password"
                          value={linkPassword}
                          onChange={(e) => setLinkPassword(e.target.value)}
                          placeholder={linkStats?.has_password ? 'Enter new password to change' : 'No password set'}
                          className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                        {linkStats?.has_password && (
                          <p className="text-xs text-gray-500 mt-1">Password is currently set</p>
                        )}
                      </div>

                      {/* Expiration */}
                      <div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                          <Clock className="w-4 h-4" />
                          Expiration date
                        </label>
                        <input
                          type="datetime-local"
                          value={linkExpires}
                          onChange={(e) => setLinkExpires(e.target.value)}
                          className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                        {linkStats?.expires_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            Current expiration: {new Date(linkStats.expires_at).toLocaleString()}
                          </p>
                        )}
                      </div>

                      {/* Disable Download */}
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={disableDownload}
                          onChange={(e) => setDisableDownload(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Download className="w-4 h-4" />
                        Disable downloads
                      </label>

                      {/* Max Views */}
                      <div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                          <Eye className="w-4 h-4" />
                          Maximum views
                        </label>
                        <input
                          type="number"
                          value={maxViews}
                          onChange={(e) => setMaxViews(e.target.value)}
                          placeholder="Unlimited"
                          min="1"
                          className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={handleUpdateLink}
                        size="sm"
                        className="flex-1"
                        disabled={loadingLink}
                      >
                        {loadingLink ? 'Updating...' : 'Update settings'}
                      </Button>
                      <Button
                        onClick={handleRevokeLink}
                        size="sm"
                        variant="outline"
                        className="flex-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        disabled={loadingLink}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Revoke link
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Slack Tab */}
          {activeTab === 'slack' && (
            <div className="space-y-4">
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                  <div>
                    <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                      Send Slack Notification
                    </h3>
                    <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                      Notify team members who have connected their Slack accounts
                    </p>
                  </div>
                </div>

                {slackConnectedUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      No Slack-connected users found
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      Users need to connect their Slack accounts in Settings
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-purple-700 dark:text-purple-300">
                      Select people to notify ({slackConnectedUsers.length} available):
                    </p>

                    <div className="max-h-64 overflow-y-auto space-y-2 border border-purple-200 dark:border-purple-700 rounded-md p-2">
                      {slackConnectedUsers.map(user => (
                        <label
                          key={user.id}
                          className="flex items-center gap-3 p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSlackUsers.has(user.id)}
                            onChange={() => toggleSlackUser(user.id)}
                            className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.name}
                              </span>
                              {user.slackUsername && (
                                <span className="text-xs text-purple-600 dark:text-purple-400">
                                  @{user.slackUsername}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {user.email}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>

                    {selectedSlackUsers.size > 0 && (
                      <div className="flex items-center justify-between p-2 bg-purple-100 dark:bg-purple-900/30 rounded">
                        <span className="text-xs text-purple-700 dark:text-purple-300">
                          {selectedSlackUsers.size} {selectedSlackUsers.size === 1 ? 'person' : 'people'} selected
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedSlackUsers(new Set())}
                          className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                        >
                          Clear all
                        </button>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={handleSendSlackNotifications}
                        disabled={selectedSlackUsers.size === 0 || sendingSlackNotifications}
                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {sendingSlackNotifications ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <MessageSquare className="w-4 h-4" />
                            Send Notifications
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
